import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileAlt,
    faArrowLeft,
    faInfoCircle,
    faCheckCircle,
    faChartLine,
    faDownload,
    faEnvelope,
    faCog,
    faSpinner,
    faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function AnalyticsReporting() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Centro de Reporting
                    </h1>
                    <p className="text-xl text-gray-600">
                        Genera reportes ejecutivos profesionales con análisis avanzados, recomendaciones de IA y exportación a PDF.
                    </p>
                </div>

                {/* Acceso */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Cómo acceder?
                    </h2>
                    <p className="text-gray-700 mb-2">
                        Para acceder al Centro de Reporting:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                        <li>Inicia sesión en tu cuenta de PetitPrime</li>
                        <li>En el menú principal, selecciona <strong>"Analytics"</strong></li>
                        <li>Haz clic en <strong>"Centro de Reportes"</strong></li>
                        <li>O accede directamente a: <code className="bg-white px-2 py-1 rounded">/statistics/reporting</code></li>
                    </ol>
                </div>

                {/* Configuración Inicial */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Configuración Inicial
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Antes de generar un reporte, necesitas configurar los siguientes parámetros:
                        </p>

                        {/* Selector de Marca */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Selector de Marca
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Selecciona la marca para la cual deseas generar el reporte:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Si solo tienes una marca, se seleccionará automáticamente</li>
                                    <li>Si tienes múltiples marcas, elige la que deseas analizar</li>
                                    <li>Todos los datos del reporte se filtrarán por la marca seleccionada</li>
                                </ul>
                            </div>
                        </div>

                        {/* Selector de Período */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Selector de Período
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Elige el período de tiempo que deseas analizar:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Semanal:</strong> Últimos 7 días</li>
                                    <li><strong>Mensual:</strong> Últimos 30 días</li>
                                    <li><strong>Trimestral:</strong> Últimos 90 días</li>
                                    <li><strong>Anual:</strong> Últimos 365 días</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    El período seleccionado determinará el rango de fechas de los datos incluidos en el reporte.
                                </p>
                            </div>
                        </div>

                        {/* Tipo de Reporte */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Tipo de Reporte
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Actualmente está disponible el <strong>Reporte Ejecutivo</strong>, que incluye:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Resumen ejecutivo con métricas clave</li>
                                    <li>Recomendaciones de IA basadas en los datos</li>
                                    <li>Gráficas de evolución temporal</li>
                                    <li>Análisis de campañas y fidelización</li>
                                    <li>Distribución de clientes por segmentación RFM</li>
                                </ul>
                                <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                                    <p className="text-xs text-gray-700">
                                        <strong>Nota:</strong> Otros tipos de reportes (Predictivo, Canales, Cohortes, etc.) están en desarrollo y estarán disponibles próximamente.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Generación de Reporte */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Generar Reporte
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Una vez configurados los parámetros, puedes generar el reporte de dos formas:
                        </p>

                        {/* Generación Síncrona */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faChartLine} className="mr-2 text-blue-600" />
                                Generación Inmediata (Síncrona)
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Haz clic en el botón <strong>"Generar Reporte"</strong> para crear el reporte inmediatamente:
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>El sistema calculará todas las métricas en tiempo real</li>
                                    <li>Verás un indicador de carga mientras se procesan los datos</li>
                                    <li>El reporte aparecerá en pantalla una vez completado</li>
                                    <li>Podrás descargarlo como PDF directamente desde la página</li>
                                </ol>
                                <div className="mt-3 flex items-start space-x-2 text-sm text-gray-600">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                                    <p>
                                        <strong>Ventaja:</strong> Ideal para análisis rápidos y cuando necesitas el reporte inmediatamente.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Generación Asíncrona */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-green-600" />
                                Generación por Email (Asíncrona)
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Para reportes más complejos o cuando prefieres recibirlo por email:
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Haz clic en <strong>"Generar y Enviar por Email"</strong></li>
                                    <li>Se creará un trabajo (job) en segundo plano</li>
                                    <li>Verás una barra de progreso con el estado del procesamiento</li>
                                    <li>Recibirás el reporte en PDF por email cuando esté listo</li>
                                    <li>También podrás descargarlo desde la lista de reportes generados</li>
                                </ol>
                                <div className="mt-3 flex items-start space-x-2 text-sm text-gray-600">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                                    <p>
                                        <strong>Ventaja:</strong> Ideal para reportes complejos o cuando no necesitas el resultado inmediatamente.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Opciones Avanzadas */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faCog} className="mr-2 text-purple-600" />
                                Opciones Avanzadas
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Puedes personalizar la generación del reporte con opciones avanzadas:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Zona Horaria:</strong> Europe/Madrid, America/Mexico_City, UTC</li>
                                    <li><strong>Idioma:</strong> Español (España), Español (México), Inglés</li>
                                    <li><strong>Moneda:</strong> EUR, MXN, USD</li>
                                    <li><strong>Granularidad:</strong> Día, Semana, Mes</li>
                                    <li><strong>Formato:</strong> JSON, HTML, PDF, CSV</li>
                                    <li><strong>Comparación:</strong> Período anterior, Año anterior, Sin comparación</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    Estas opciones están disponibles en el panel desplegable de "Opciones Avanzadas".
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenido del Reporte */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Contenido del Reporte Ejecutivo
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            El Reporte Ejecutivo incluye las siguientes secciones:
                        </p>

                        {/* Resumen Ejecutivo */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Resumen Ejecutivo
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Métricas clave presentadas en tarjetas visuales:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Ventas Totales:</strong> Con variación porcentual respecto al período anterior</li>
                                    <li><strong>Ticket Promedio:</strong> Valor promedio de cada transacción</li>
                                    <li><strong>Nuevos Clientes:</strong> Cantidad de clientes nuevos registrados</li>
                                    <li><strong>Clientes Recurrentes:</strong> Clientes que han comprado más de una vez</li>
                                    <li><strong>Clientes Perdidos:</strong> Clientes que no han comprado en el período</li>
                                    <li><strong>Satisfacción Promedio:</strong> NPS o rating promedio de clientes</li>
                                </ul>
                            </div>
                        </div>

                        {/* Recomendaciones de IA */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                                Recomendaciones de IA
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    El sistema analiza tus datos y genera recomendaciones accionables:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Recomendaciones basadas en tendencias identificadas</li>
                                    <li>Sugerencias para mejorar métricas específicas</li>
                                    <li>Alertas sobre oportunidades de crecimiento</li>
                                    <li>Consejos para optimizar campañas y fidelización</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    Estas recomendaciones se generan automáticamente usando análisis de datos e inteligencia artificial.
                                </p>
                            </div>
                        </div>

                        {/* Gráficas de Evolución */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faChartLine} className="mr-2 text-blue-600" />
                                Gráficas de Evolución
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Visualizaciones temporales que muestran tendencias:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Evolución de Ventas:</strong> Gráfico de línea mostrando ventas por día/semana/mes</li>
                                    <li><strong>Distribución de Clientes:</strong> Gráfico circular (pie) con segmentación RFM</li>
                                    <li><strong>Tasa de Retorno:</strong> Porcentaje de clientes que vuelven a comprar</li>
                                    <li><strong>Satisfacción:</strong> Evolución del nivel de satisfacción de clientes</li>
                                </ul>
                            </div>
                        </div>

                        {/* Métricas de Campañas */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Métricas de Campañas
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Análisis del rendimiento de tus campañas de marketing:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Tasa de apertura de emails</li>
                                    <li>Tasa de clic (CTR)</li>
                                    <li>ROI (Retorno de Inversión)</li>
                                    <li>Conversiones por campaña</li>
                                </ul>
                            </div>
                        </div>

                        {/* Métricas de Fidelización */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Métricas de Fidelización
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Análisis del programa de puntos y recompensas:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Puntos acumulados totales</li>
                                    <li>Puntos canjeados</li>
                                    <li>Tasa de redención</li>
                                    <li>Premios más populares</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Exportación y Descarga */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Exportación y Descarga
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Una vez generado el reporte, puedes exportarlo en diferentes formatos:
                        </p>

                        {/* PDF Local */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faDownload} className="mr-2 text-red-600" />
                                Descarga PDF Local
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Para descargar el reporte como PDF directamente desde el navegador:
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Haz clic en el botón <strong>"Descargar PDF"</strong></li>
                                    <li>El sistema generará un PDF profesional con branding PetitPrime</li>
                                    <li>El archivo se descargará automáticamente a tu carpeta de descargas</li>
                                    <li>El PDF incluye todas las secciones del reporte con formato corporativo</li>
                                </ol>
                                <p className="text-sm text-gray-600 mt-2">
                                    <strong>Formato:</strong> El PDF incluye header con logo, información de la marca, fecha de generación, y todas las métricas y gráficas organizadas profesionalmente.
                                </p>
                            </div>
                        </div>

                        {/* PDF del Backend */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faDownload} className="mr-2 text-blue-600" />
                                Descarga PDF del Backend
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Si generaste el reporte de forma asíncrona (por email):
                                </p>
                                <ol className="list-decimal list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Accede a la lista de reportes generados</li>
                                    <li>Busca el reporte que deseas descargar</li>
                                    <li>Haz clic en el botón de descarga junto al reporte</li>
                                    <li>El PDF generado por el backend se descargará directamente</li>
                                </ol>
                                <p className="text-sm text-gray-600 mt-2">
                                    <strong>Ventaja:</strong> Los PDFs generados por el backend suelen tener mejor calidad y formato optimizado.
                                </p>
                            </div>
                        </div>

                        {/* Historial de Reportes */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Historial de Reportes Generados
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Puedes acceder a un historial de todos los reportes que has generado:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Ver lista de reportes anteriores</li>
                                    <li>Filtrar por marca y período</li>
                                    <li>Descargar reportes anteriores en cualquier momento</li>
                                    <li>Comparar reportes de diferentes períodos</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Interpretación de Datos */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        5. Interpretación de los Datos
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para aprovechar al máximo los reportes ejecutivos:
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Variación de Ventas:</strong> Una variación positiva indica crecimiento. Si es negativa, analiza las causas (estacionalidad, cambios en el mercado, etc.) y toma acciones correctivas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Recomendaciones de IA:</strong> No ignores las recomendaciones generadas. Están basadas en análisis de datos y pueden revelar oportunidades importantes.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Distribución de Clientes:</strong> Una distribución saludable debería tener más clientes en segmentos de alto valor (Champions, Loyal Customers). Si hay muchos en "At Risk" o "Lost", considera campañas de recuperación.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Comparación Periódica:</strong> Genera reportes mensuales o trimestrales para identificar tendencias a largo plazo y evaluar el impacto de tus estrategias.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        6. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">El reporte no se genera</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de haber seleccionado una marca y un período. Verifica que la marca tenga datos de ventas y clientes. Si acabas de crear tu cuenta, es normal que el reporte muestre valores en cero hasta que tengas transacciones registradas.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">La descarga de PDF no funciona</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que tu navegador permita descargas. Si el problema persiste, intenta usar la opción de generación por email y descarga el PDF desde el historial de reportes.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">El job asíncrono se queda en "Procesando"</h4>
                                <p className="text-gray-700 text-sm">
                                    Los reportes complejos pueden tardar varios minutos. Si después de 10-15 minutos sigue procesando, intenta generar un nuevo reporte o contacta con soporte.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Los datos parecen incorrectos</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que hayas seleccionado la marca correcta y que el período sea el adecuado. Los datos se calculan desde los tickets de compra registrados en el sistema.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/analytics/ofertas"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Métricas de Ofertas
                    </Link>
                    <Link
                        to="/documentation/analytics"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Volver a Analytics
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default AnalyticsReporting;

